/**
 * תמחור שורת תשלום (כרטיס אשראי) — ריבית תשלומים מטבלת הריבית + סיווג J4/J5.
 * תואם לנוסחה ההיסטורית ב-CardsContext: (principal * rate * n) / 12
 *
 * מדיניות קמפיין / תשלומים: 1–3 ללא חובת שיעור בטבלה; מעל 3 (4–12) חובה interestTable[n].
 */

import { applyInstallmentsFee, legacyAppRound2 } from "./pricingEngine.js";
import {
  jsonSafe,
  recordPaymentInstallmentPricingAudit,
  shouldCapturePricingEngineAudit,
} from "./pricingEngineAuditStore.js";

/** 1–3 inclusive = ללא ריבית (אם אין שיעור בטבלה → 0). מעל זה (4–12) חובה שיעור. */
export const INTEREST_FREE_MAX_INSTALLMENTS = 3;

/** @param {Record<string, unknown>|null|undefined} table */
export function interestCellForPayments(table, n) {
  if (!table || typeof table !== "object") return null;
  const k = Math.floor(Number(n)) || 1;
  const v = table[k] ?? table[String(k)];
  if (v == null || v === "") return null;
  const num = Number(v);
  return Number.isFinite(num) ? num : null;
}

/**
 * האם מותר לבחור n תשלומים בלי לחסום.
 * 1–3: מותר גם בלי שיעור בטבלה.
 * מעל 3: חובה שיעור חיובי וסופי ב-interestTable[n] (fail-closed).
 */
export function hasRequiredInterestTableRate(interestTable, numberOfPayments) {
  const n = Math.max(1, Math.floor(Number(numberOfPayments)) || 1);
  if (n <= INTEREST_FREE_MAX_INSTALLMENTS) return true;
  const cell = interestCellForPayments(interestTable, n);
  return cell != null && Number.isFinite(cell) && cell > 0;
}

/**
 * אפשרויות תשלומים לקמפיין: תמיד 1–3; מעל 3 רק כשיש שיעור חיובי בטבלה.
 * בלי טבלה — לא מציגים 4–12 כ־0-ריבית (בניגוד ל־fallback הישן 1–12).
 */
export function buildCampaignInstallmentOptions(interestTable) {
  const options = new Set([1, 2, 3]);
  if (interestTable && typeof interestTable === "object") {
    for (const key of Object.keys(interestTable)) {
      const n = Math.floor(Number(key));
      if (!Number.isFinite(n) || n < 1 || n > 12) continue;
      if (n > INTEREST_FREE_MAX_INSTALLMENTS && !hasRequiredInterestTableRate(interestTable, n)) {
        continue;
      }
      options.add(n);
    }
  }
  return Array.from(options).sort((a, b) => a - b);
}

/**
 * אחוז שנתי מהטבלה כשברירת יחידה (כמו CardsContext: /100 לפני הנוסחה).
 * @returns {number} שבר עשרוני (למשל 0.05 ל-5%)
 */
export function resolveAnnualInterestFractionFromTable(interestTable, numberOfPayments) {
  const n = Math.floor(Number(numberOfPayments)) || 1;
  if (n < 0 || n > 12) return 0;
  const cell = interestCellForPayments(interestTable, n);
  if (cell == null) return 0;
  return cell / 100;
}

/**
 * Live card quote. Above 3 installments a missing or non-positive table rate
 * is a failed calculation: interest is not reported as 0.
 */
export function quoteCardInstallment({ principalIls, numberOfPayments, interestTable } = {}) {
  const n = Math.max(1, Math.floor(Number(numberOfPayments)) || 1);
  const principal = Number(principalIls);
  let table = interestTable;
  if (typeof table === "string") {
    try {
      table = JSON.parse(table);
    } catch {
      table = null;
    }
  }
  const annualRate = interestCellForPayments(table, n);
  const rateMissing =
    n > INTEREST_FREE_MAX_INSTALLMENTS &&
    !(annualRate != null && Number.isFinite(annualRate) && annualRate > 0);
  if (!Number.isFinite(principal) || principal <= 0) {
    return {
      ok: false,
      rateMissing: false,
      principalIls: Number.isFinite(principal) ? principal : 0,
      numberOfPayments: n,
      paymentInterest: null,
      amountWithInterest: null,
      perPaymentIls: null,
      annualRate,
      error: null,
    };
  }
  if (rateMissing) {
    return {
      ok: false,
      rateMissing: true,
      principalIls: principal,
      numberOfPayments: n,
      paymentInterest: null,
      amountWithInterest: null,
      perPaymentIls: null,
      annualRate,
      error: `לא ניתן לחשב ריבית ל-${n} תשלומים. חסר שיעור בטבלת הריבית.`,
    };
  }
  const priced = runPaymentInstallmentPricing({
    principalIls: principal,
    numberOfPayments: n,
    interestTable: table,
  });
  return {
    ok: true,
    rateMissing: false,
    principalIls: principal,
    numberOfPayments: n,
    paymentInterest: priced.paymentInterest,
    amountWithInterest: priced.amountWithInterest,
    perPaymentIls: n > 0 ? priced.amountWithInterest / n : priced.amountWithInterest,
    annualRate,
    error: null,
  };
}

export function computeInstallmentInterestRaw(principalIls, numberOfPayments, annualFraction) {
  const p = Number(principalIls);
  const n = Math.floor(Number(numberOfPayments)) || 1;
  const r = Number(annualFraction) || 0;
  if (!Number.isFinite(p) || p <= 0 || !Number.isFinite(n) || n < 1) return 0;
  return (p * r * n) / 12;
}

/**
 * @param {{
 *   principalIls: number,
 *   numberOfPayments: number,
 *   interestTable: Record<string, unknown>|null|undefined,
 *   roundMoney?: (n: number) => number,
 *   settlementCurrency?: string,
 *   fxApplied?: boolean,
 * }} input
 */
export function runPaymentInstallmentPricing(input) {
  const roundMoney =
    typeof input.roundMoney === "function" ? input.roundMoney : legacyAppRound2;
  const auditSteps = ["payment:enter"];
  const principal = Number(input.principalIls);
  const n = Math.max(1, Math.floor(Number(input.numberOfPayments)) || 1);
  const settlementCurrency = String(
    input.settlementCurrency ?? "ILS"
  ).toUpperCase();
  const fxApplied = Boolean(input.fxApplied);

  const annualFraction = resolveAnnualInterestFractionFromTable(
    input.interestTable,
    n
  );
  const rawInterest = computeInstallmentInterestRaw(principal, n, annualFraction);
  const paymentInterest = roundMoney(rawInterest);
  const amountWithInterest = roundMoney(principal + paymentInterest);

  /** J4 = חיוב מיידי (תשלום אחד); J5 = תוכנית תשלומים / דורש אישור לפי מוסכמות הסולק */
  const paymentAction = n === 1 ? "J4" : "J5";

  auditSteps.push(`payment:installments_applied:n:${n}`);
  auditSteps.push(`payment:installment_annual_table_pct:${annualFraction * 100}`);
  auditSteps.push(`payment:action:${paymentAction}`);
  auditSteps.push("payment:final_charge_resolved");
  if (fxApplied) {
    auditSteps.push("payment:fx_settlement_applied");
  } else {
    auditSteps.push("payment:fx_settlement:not_applicable");
  }

  const result = {
    principalIls: Number.isFinite(principal) ? principal : 0,
    numberOfPayments: n,
    paymentInterest,
    amountWithInterest,
    installmentFeePercent: annualFraction * 100,
    installmentFeeAmount: paymentInterest,
    finalChargePrice: amountWithInterest,
    paymentAction,
    settlementAmount: amountWithInterest,
    settlementCurrency,
    fxApplied,
    auditSteps,
  };

  if (shouldCapturePricingEngineAudit()) {
    try {
      recordPaymentInstallmentPricingAudit({
        meta: input.pricingAuditMeta ?? null,
        input: jsonSafe(input),
        result: jsonSafe(result),
      });
    } catch (e) {
      console.warn("[pricing-engine-audit] record payment failed", e);
    }
  }

  return result;
}

/**
 * עמלת ניכוי / אשראי (תשלומים) על בסיס — אותו מסלול כמו applyInstallmentsFee במנוע.
 */
export function runPaymentCreditClearingOnBase({
  baseForFeeIls,
  feePercent,
  roundMoney = legacyAppRound2,
}) {
  const auditSteps = ["payment:cc_clearing:enter"];
  const inst = applyInstallmentsFee(
    baseForFeeIls,
    feePercent ?? 0,
    roundMoney
  );
  auditSteps.push(`payment:cc_clearing:pct:${inst.percentApplied}`);
  auditSteps.push("payment:final_charge_resolved");
  return {
    creditCardFee: inst.amount,
    percentApplied: inst.percentApplied,
    auditSteps,
  };
}

/**
 * בונה קלט תיאורי לתשלום (לא חובה ל-run — לשימוש בבדיקות / לוג).
 */
export function buildPaymentPricingInput({
  principalIls,
  numberOfPayments,
  interestTable,
  creditClearingPercent,
  settlementCurrency = "ILS",
}) {
  return {
    principalIls: Number(principalIls) || 0,
    numberOfPayments: Math.max(1, Math.floor(Number(numberOfPayments)) || 1),
    interestTable: interestTable ?? null,
    creditClearingPercent:
      creditClearingPercent != null && Number.isFinite(Number(creditClearingPercent))
        ? Number(creditClearingPercent)
        : null,
    settlementCurrency: String(settlementCurrency || "ILS").toUpperCase(),
  };
}
